// src/utils/share/shareUtils.ts
// -----------------------------------------------------------------------------
// Canonical URL + share helpers (SSR-safe, content-based routing)
// Target routes:
//   /content/[slug]
//   /lit/<username>/content/<slug>?type=read|video|voice&...
// -----------------------------------------------------------------------------
 
export type ContentType = "read" | "video" | "voice" | "media";


/**
 * Get canonical origin from env, e.g. https://townlit.com
 * We store a sans-slash variant for reuse.
 */
export function getCanonicalOrigin(): URL | null {
  try {
    const raw = process.env.NEXT_PUBLIC_BACKEND_BASE;
    if (!raw) return null;
    const u = new URL(raw);
    (u as any).__sansSlash = u.toString().replace(/\/+$/, "");
    return u;
  } catch {
    return null;
  }
}

/**
 * Force any href onto canonical origin, keeping path/search/hash.
 * On localhost, it keeps localhost but normalizes protocol/host for real origin.
 */
export function toCanonical(href: string): string {
  try {
    const base =
      typeof window !== "undefined" ? window.location.href : "http://localhost/";
    const u = new URL(href, base);
    const canon = getCanonicalOrigin();
    if (!canon) return u.toString();

    const canonHost =
      (canon as any).__sansSlash ||
      canon.toString().replace(/\/+$/, "");
    const canonUrl = new URL(canonHost);

    const isLocal = ["localhost", "127.0.0.1"].includes(u.hostname);
    const hostDiff = u.host !== canonUrl.host;

    if (isLocal || hostDiff) {
      u.protocol = canonUrl.protocol;
      u.hostname = canonUrl.hostname;
      u.port = canonUrl.port;
      u.username = "";
      u.password = "";
    }
    return u.toString();
  } catch {
    return href;
  }
}

/**
 * Current page as canonical href (browser only).
 */
export function pageCanonicalHref(): string {
  if (typeof window === "undefined") return "";
  return toCanonical(window.location.href);
}

/**
 * Add or replace ?t=<seconds> in a content URL.
 */
export function withTimestamp(href: string, seconds: number): string {
  try {
    const u = new URL(href);
    u.searchParams.delete("t");
    if (Number.isFinite(seconds) && seconds > 0) {
      u.searchParams.set("t", String(Math.floor(seconds)));
    }
    return u.toString();
  } catch {
    return href;
  }
}

/**
 * Build a share text line:
 * "@handle — title\n<url>"
 */
export function buildShareText(
  title: string,
  ownerHandle?: string,
  href?: string
): string {
  const prefix = ownerHandle ? `${ownerHandle} — ${title}` : title;
  return href ? `${prefix}\n${href}` : prefix;
}

/* -------------------------------------------------------------------------- */
/* Privacy + param helpers                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Consider endpoints with "/me/" in path as private.
 */
function isPrivateEndpoint(e: string): boolean {
  try {
    return /\/me(\/|$)/i.test(e);
  } catch {
    return false;
  }
}

/**
 * Only carry ?e if the endpoint is not private.
 */
function shouldCarryE(srcHref: string): boolean {
  try {
    const src = new URL(srcHref);
    const e = src.searchParams.get("e") || "";
    if (!e) return false;
    return !isPrivateEndpoint(e);
  } catch {
    return false;
  }
}

/**
 * If URL already has ?k, use it.
 */
function pickExistingK(srcHref: string): string | undefined {
  try {
    const u = new URL(srcHref);
    const k = u.searchParams.get("k");
    return k && k.trim() ? k.trim() : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Infer content type from URL (?type=...), fallback to "read".
 */
function inferContentTypeFromUrl(u: URL): ContentType {
  const raw = (u.searchParams.get("type") || "").toLowerCase();
  if (raw === "video") return "video";
  if (raw === "voice") return "voice";
  return "read";
}

/**
 * Heuristic "k" (selection key) from URL.
 * NOTE: This is mainly for testimonies; other sections should set k explicitly.
 */
function inferKFromUrl(u: URL): string | undefined {
  const p = (u.pathname || "").toLowerCase();
  const t = (u.searchParams.get("type") || "").toLowerCase();

  // Prefer explicit type
  if (t === "video") return "testimonies.video";
  if (t === "voice") return "testimonies.voice";
  if (t === "read") return "testimonies.written";

  // Legacy support (if any older URLs still exist)
  if (p.includes("/watch/")) return "testimonies.video";
  if (p.includes("/read/") || p.includes("/content/")) return "testimonies.written";

  return undefined;
}

/**
 * Map any private ?e (e.g. /me/...) → public profile reader params.
 * This is used when sharing links that were opened via private endpoints.
 */
function inferPublicParamsFromPrivate(
  srcHref: string
): { e: string; s?: string; k?: string; o?: string } | null {
  try {
    const src = new URL(srcHref);
    const e = src.searchParams.get("e") || "";
    if (!e || !isPrivateEndpoint(e)) return null;

    const existingK = pickExistingK(srcHref);
    const inferredK = existingK ?? inferKFromUrl(src);

    return {
      e: "/profiles/members/profile",
      s: "profile",
      o: "user",
      ...(inferredK ? { k: inferredK } : {}),
    };
  } catch {
    return null;
  }
}

/**
 * Copy selected query keys from src → target.
 */
function carryQueryParamsFiltered(
  srcHref: string,
  targetHref: string,
  keys: string[]
): string {
  try {
    const src = new URL(srcHref);
    const dst = new URL(targetHref, srcHref);
    for (const k of keys) {
      const val = src.searchParams.get(k);
      if (val !== null) dst.searchParams.set(k, val);
    }
    return dst.toString();
  } catch {
    return targetHref;
  }
}

/**
 * Check if current URL uses private ?e endpoint.
 */
function isPrivateMeContent(srcHref: string): boolean {
  try {
    const u = new URL(srcHref);
    const e = u.searchParams.get("e") || "";
    return isPrivateEndpoint(e);
  } catch {
    return false;
  }
}

/* -------------------------------------------------------------------------- */
/* Deep-link builders (content-only)                                          */
/* -------------------------------------------------------------------------- */

/**
 * Universal content deep-link builder:
 *
 *   /lit/<prefixUsername>/content/<slug>?type=read|video|voice&...
 *
 * - Keeps t, s, k, o, lang, focus, a, p from the canonical URL.
 * - Keeps/normalizes e:
 *    • If public: preserve ?e
 *    • If private: map to /profiles/members/profile + profile params
 *
 * - If prefixUsername + slug are provided, builds `/lit/.../content/...`.
 * - If only prefixUsername + section are provided, builds profile anchor:
 *    `/lit/<prefix>#<section>`
 * - Otherwise returns the canonical URL as fallback.
 */
export function bestContentShareHref(opts: {
  prefixUsername?: string;
  contentSlug?: string;
  section?: string;
  canonicalFallbackHref: string;
  type?: ContentType;
}): string {
  const canon = toCanonical(opts.canonicalFallbackHref);

  const originUrl = getCanonicalOrigin();
  const origin =
    (originUrl && ((originUrl as any).__sansSlash as string)) ||
    (originUrl ? originUrl.toString().replace(/\/+$/, "") : "");

  let src: URL;
  try {
    src = new URL(canon);
  } catch {
    // If URL is malformed, just return what we have.
    return canon;
  }

  const privateMe = isPrivateMeContent(canon);
  const inferred = privateMe ? inferPublicParamsFromPrivate(canon) : null;

  // Content type: explicit from opts or inferred from URL
  const contentType: ContentType =
    opts.type ?? inferContentTypeFromUrl(src);

  // 1) Deep route: /lit/<username>/content/<slug>
  if (opts.prefixUsername && (opts.contentSlug || inferred)) {
    const slug = (opts.contentSlug || "").trim();
    const finalSlug =
      slug || (privateMe && inferred ? opts.prefixUsername! : "");

    const basePath = origin
      ? `${origin}/lit/${encodeURIComponent(
          opts.prefixUsername
        )}/content/${encodeURIComponent(finalSlug)}`
      : `/lit/${encodeURIComponent(
          opts.prefixUsername
        )}/content/${encodeURIComponent(finalSlug)}`;

    let out = basePath;

    // Carry common reader/viewer parameters
    const baseKeys = ["t", "s", "k", "o", "lang", "focus", "a", "p"];
    out = carryQueryParamsFiltered(canon, out, baseKeys);

    try {
      const u = new URL(out, out);

      // Ensure type is set
      u.searchParams.set("type", contentType);

      // Handle e (endpoint)
      if (shouldCarryE(canon)) {
        const eVal = src.searchParams.get("e");
        if (eVal) u.searchParams.set("e", eVal);
      } else if (inferred) {
        // Map private endpoint to public profile endpoint
        u.searchParams.set("e", inferred.e);
        if (inferred.s) u.searchParams.set("s", inferred.s);
        if (inferred.o) u.searchParams.set("o", inferred.o);

        // Only set k if not already provided
        if (inferred.k && !u.searchParams.get("k")) {
          u.searchParams.set("k", inferred.k);
        }
      }

      out = u.toString();
    } catch {
      // If the URL construction fails, return the base path at least.
      return basePath;
    }

    return out;
  }

  // 2) Profile landing: /lit/<username>#<section>
  if (opts.prefixUsername && opts.section) {
    return origin
      ? `${origin}/lit/${encodeURIComponent(
          opts.prefixUsername
        )}#${encodeURIComponent(opts.section)}`
      : `/lit/${encodeURIComponent(
          opts.prefixUsername
        )}#${encodeURIComponent(opts.section)}`;
  }

  // 3) Fallback: just return the canonical URL
  return canon;
}

/* -------------------------------------------------------------------------- */
/* Share dispatcher (native → clipboard → mailto fallback)                    */
/* -------------------------------------------------------------------------- */

/**
 * Try native Web Share API, then clipboard, then mailto as last resort.
 */
export async function shareWithFallback(opts: {
  title: string;
  text: string;
  url?: string;
}) {
  const nav: any = typeof navigator !== "undefined" ? navigator : null;

  // 1) Native share (if available)
  if (nav?.share) {
    const canShare =
      !nav.canShare ||
      nav.canShare({ title: opts.title, text: opts.text, url: opts.url });

    if (canShare) {
      await nav.share({
        title: opts.title,
        text: opts.text,
        url: opts.url,
      });
      return;
    }
  } 

  // 2) Clipboard fallback
  if (nav?.clipboard?.writeText && (opts.url || opts.text)) {
    await nav.clipboard.writeText(opts.url ?? opts.text);
    return;
  }

  // 3) mailto fallback
  const mailto = `mailto:?subject=${encodeURIComponent(
    opts.title
  )}&body=${encodeURIComponent(
    opts.text + (opts.url ? `\n${opts.url}` : "")
  )}`;

  if (typeof window !== "undefined") {
    window.location.href = mailto;
  }
}


// -----------------------------------------------------------------------------
// Utilities
// -----------------------------------------------------------------------------
export function withQueryParam(href: string, key: string, value?: string): string {
  try {
    const u = new URL(href, typeof window !== "undefined" ? window.location.href : "http://localhost/");
    u.searchParams.delete(key);
    if (value && value.trim()) u.searchParams.set(key, value.trim());
    return u.toString();
  } catch {
    return href;
  }
}
